/**
 * /preview/:domain — public viral preview hook.
 *
 * Anyone can hit toprankpulse.com/preview/stripe.com and instantly see a
 * shareable AI Visibility snapshot for that domain. No signup, no scan
 * required. Each preview has share buttons that propagate the URL to
 * Twitter, LinkedIn, WhatsApp — the share content includes the prospect's
 * own domain, which creates a perfect viral loop:
 *   Marketer X shares preview of stripe.com → stripe.com employee sees it →
 *   they share their score → their followers see it → loop.
 *
 * Replicates Stape's "Audit tracking in seconds" hook + competitors'
 * borrowed-authority pattern but as a real product page.
 */
import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, Share2, Twitter, Linkedin, MessageCircle, Copy } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { AISnapshotCard } from "../components/AISnapshotCard";
import { TrustBadgeRow } from "../components/TrustBadgeRow";

const COPY = {
  sk: {
    eyebrow: "AI SEARCH SNAPSHOT",
    h1: "Takto vidia AI vyhľadávače tento web",
    sub: "Google, ChatGPT, Perplexity, Gemini, Bing AI — všetkých 5 motorov za 2 sekundy. Bez registrácie.",
    full_cta: "Spustiť kompletný audit",
    share_h: "Zdieľaj snapshot",
    share_t: "Pozri sa, ako si na tom v AI vyhľadávaní 👇",
    cta_yourself: "Skontroluj svoj web zadarmo",
    copy_done: "Skopírované!",
  },
  en: {
    eyebrow: "AI SEARCH SNAPSHOT",
    h1: "Here's how AI engines see this site",
    sub: "Google, ChatGPT, Perplexity, Gemini, Bing AI — all 5 engines in 2 seconds. No signup.",
    full_cta: "Run the full audit",
    share_h: "Share this snapshot",
    share_t: "See how this site ranks across AI search 👇",
    cta_yourself: "Check your site for free",
    copy_done: "Copied!",
  },
};

export default function PreviewPage() {
  const { domain: rawDomain } = useParams();
  const [params] = useSearchParams();
  const { language } = useLanguage();
  const t = COPY[language] || COPY.en;
  const embed = params.get("embed") === "1";

  const domain = useMemo(() => {
    if (!rawDomain) return "";
    return decodeURIComponent(rawDomain)
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .replace(/\/$/, "")
      .split("/")[0]
      .toLowerCase();
  }, [rawDomain]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      const btn = document.getElementById("preview-copy-btn");
      if (btn) {
        const orig = btn.innerText;
        btn.innerText = t.copy_done;
        setTimeout(() => { btn.innerText = orig; }, 1500);
      }
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white" data-testid="preview-page">
      {!embed && (
        <header className="border-b border-[var(--color-border)] py-4 px-6">
          <Link to="/" className="font-black text-lg font-['Cabinet_Grotesk']">TopRankPulse</Link>
        </header>
      )}

      <section className="max-w-3xl mx-auto px-4 py-14">
        <p className="text-xs font-mono tracking-[0.3em] text-[#a855f7] mb-3 text-center" data-testid="preview-eyebrow">
          {t.eyebrow}
        </p>
        <h1 className="text-3xl sm:text-5xl font-black font-['Cabinet_Grotesk'] mb-3 leading-tight text-center" data-testid="preview-h1">
          {t.h1}
        </h1>
        <p className="text-base sm:text-lg text-[var(--color-text-muted)] max-w-xl mx-auto mb-3 text-center">
          {t.sub}
        </p>
        <p className="text-xs font-mono text-center mb-8" data-testid="preview-domain">
          <span className="text-[var(--color-text-muted)]">→</span>{" "}
          <span className="text-white font-bold">{domain}</span>
        </p>

        {/* The viral asset */}
        <AISnapshotCard domain={domain} />

        {/* Share row */}
        <div className="mt-8 text-center">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--color-text-muted)] mb-3">
            <Share2 className="w-3 h-3 inline mr-1" />{t.share_h}
          </p>
          <div className="flex items-center justify-center gap-2 flex-wrap" data-testid="preview-share">
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(t.share_t + " " + shareUrl)}`}
              target="_blank" rel="noreferrer"
              className="px-3 py-2 bg-[var(--color-surface)] border border-[var(--color-border)] rounded text-xs hover:border-white inline-flex items-center gap-2"
              data-testid="preview-share-twitter"
            >
              <Twitter className="w-3 h-3" /> Twitter / X
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
              target="_blank" rel="noreferrer"
              className="px-3 py-2 bg-[var(--color-surface)] border border-[var(--color-border)] rounded text-xs hover:border-white inline-flex items-center gap-2"
              data-testid="preview-share-linkedin"
            >
              <Linkedin className="w-3 h-3" /> LinkedIn
            </a>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(t.share_t + " " + shareUrl)}`}
              target="_blank" rel="noreferrer"
              className="px-3 py-2 bg-[var(--color-surface)] border border-[var(--color-border)] rounded text-xs hover:border-white inline-flex items-center gap-2"
              data-testid="preview-share-whatsapp"
            >
              <MessageCircle className="w-3 h-3" /> WhatsApp
            </a>
            <button
              type="button"
              id="preview-copy-btn"
              onClick={copy}
              className="px-3 py-2 bg-[var(--color-surface)] border border-[var(--color-border)] rounded text-xs hover:border-white inline-flex items-center gap-2"
              data-testid="preview-share-copy"
            >
              <Copy className="w-3 h-3" /> Copy link
            </button>
          </div>
        </div>

        {/* Primary CTA back to the funnel */}
        <div className="mt-10 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#FFE600] text-black font-black uppercase tracking-wider hover:bg-yellow-300 transition-colors"
            data-testid="preview-primary-cta"
          >
            {t.cta_yourself} <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-[var(--color-text-muted)] text-xs mt-3 font-mono">No credit card · 30-second scan</p>
        </div>

        <div className="mt-12">
          <TrustBadgeRow language={language} compact />
        </div>
      </section>
    </div>
  );
}
